import React, { useState, useEffect } from 'react';
import { Cookie, Settings, Check, AlertTriangle } from 'lucide-react';
import { Button } from '../lp2/ui/button.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../lp2/ui/dialog.tsx';
import { Switch } from '../lp2/ui/switch.tsx';
import { Label } from '../lp2/ui/label.tsx';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowBanner(true);
    } else {
      const preferences = JSON.parse(cookieConsent);
      setCookiePreferences(preferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setCookiePreferences(necessaryOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
    setShowSettings(false);
    setShowBanner(false);
  };

  const handleTogglePreference = (type) => {
    if (type === 'necessary') return; // Can't disable necessary cookies
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg p-4 md:p-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Wir verwenden Cookies
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Wir verwenden Cookies, um Ihre Erfahrung auf unserer Website zu verbessern. 
                  Einige Cookies sind für die Funktionalität der Website erforderlich, andere helfen uns, 
                  die Website zu optimieren und Ihnen personalisierte Inhalte anzubieten.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-green-600" />
                    Notwendig
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-yellow-600" />
                    Analytics
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-yellow-600" />
                    Marketing
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Einstellungen
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
              >
                Nur notwendige
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Cookie-Einstellungen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="text-sm text-gray-600">
              <p className="mb-4">
                Hier können Sie Ihre Cookie-Einstellungen anpassen. Notwendige Cookies sind für die 
                Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
              </p>
            </div>

            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-green-600" />
                  <Label className="font-medium">Notwendige Cookies</Label>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                </p>
              </div>
              <Switch checked={true} disabled />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <Label className="font-medium">Analytics Cookies</Label>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, 
                  indem Informationen anonym gesammelt und gemeldet werden.
                </p>
              </div>
              <Switch
                checked={cookiePreferences.analytics}
                onCheckedChange={() => handleTogglePreference('analytics')}
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <Label className="font-medium">Marketing Cookies</Label>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies werden verwendet, um Besuchern relevante Anzeigen und Marketingkampagnen 
                  bereitzustellen. Diese Cookies verfolgen Besucher über Websites hinweg.
                </p>
              </div>
              <Switch
                checked={cookiePreferences.marketing}
                onCheckedChange={() => handleTogglePreference('marketing')}
              />
            </div>

            {/* Functional Cookies */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <Label className="font-medium">Funktionale Cookies</Label>
                </div>
                <p className="text-sm text-gray-600">
                  Diese Cookies ermöglichen erweiterte Funktionalitäten und Personalisierung, 
                  wie z.B. Live-Chat, Einstellungen speichern und erweiterte Benutzerfunktionen.
                </p>
              </div>
              <Switch
                checked={cookiePreferences.functional}
                onCheckedChange={() => handleTogglePreference('functional')}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSavePreferences}>
                Einstellungen speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner; 